import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card, CardContent, CardHeader } from '@cortex/web-design-system/components/ui/Card';
import { Checkbox } from '@cortex/web-design-system/components/ui/Checkbox';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import { usePmoFlowTrend } from '../../../hooks/usePmoFlow.js';
import { hasMeasuredDays, sortPmoFlowSegments, toPointsPerDay } from './segment-utils.js';
import { PMO_FLOW_WAIT_TOTAL_TARGET } from '../../../types/pmo-flow.js';
import type { PmoFlowSegmentStat, PmoFlowTrendTarget } from '../../../types/pmo-flow.js';

/** トレンドチャートの高さ（Tailwind の h-72 と一致させる）。 @graph-connects none */
const CHART_HEIGHT_CLASS = 'h-72';

/** p50 系列の色（作業時間と同じティール）。 @graph-connects none */
const P50_COLOR = '#1B9E9E';
/** p90 系列の色（待ち時間と同じ暖色）。 @graph-connects none */
const P90_COLOR = '#E8743B';
/** pt 正規化系列の色（第2軸）。 @graph-connects none */
const PER_POINT_COLOR = '#B0843B';

export interface PhaseTrendCardProps {
  /** サマリーから得た区間統計（計測できている区間だけがセレクトに並ぶ） */
  segments: PmoFlowSegmentStat[];
  /** 集計対象月数 */
  months: number;
}

/**
 * 選択区間の月次コホートトレンド本体。区間が確定してから描画されるため、
 * 計測できる区間が 1 つも無いときにクエリを発火させない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 選択されたPMOフェーズ区間の月次コホート折れ線（p50/p90日数と第2軸のpt正規化p50）を描画する
 * @graph-connects usePmoFlowTrend [calls] 区間別月次トレンド取得フック呼び出し
 * @graph-connects LineChart [calls] 月次コホート折れ線描画
 */
function PhaseTrendChart({
  segment,
  months,
  showP90,
}: {
  segment: PmoFlowTrendTarget;
  months: number;
  showP90: boolean;
}) {
  const { t } = useTranslation();
  const { data, isLoading } = usePmoFlowTrend({ segment, months });

  if (isLoading) {
    return (
      <div
        className={`${CHART_HEIGHT_CLASS} animate-pulse rounded-lg bg-[var(--color-bg-elevated)]`}
        data-testid="pmo-flow-trend-skeleton"
      />
    );
  }

  const points = data?.points ?? [];
  if (points.length === 0) {
    return (
      <div
        className={`${CHART_HEIGHT_CLASS} flex items-center justify-center rounded-lg border border-dashed border-[var(--color-border)]`}
      >
        <p className="text-[var(--color-text-muted)]">{t('status.noData')}</p>
      </div>
    );
  }

  // 一覧と同じく pt/日（大きいほど速い）で読ませるため、API の営業日/pt を反転して持たせる
  const chartPoints = points.map((point) => ({
    ...point,
    p50PointsPerDay: toPointsPerDay(point.p50DaysPerPoint ?? undefined) ?? null,
  }));
  // pt 正規化値が 1 点でもあるときだけ第2軸を出す（全 null の区間で空軸を出さない）
  const hasPerPoint = chartPoints.some((point) => point.p50PointsPerDay !== null);

  return (
    <div className={CHART_HEIGHT_CLASS}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartPoints} margin={{ top: 8, right: 16, left: 8, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
          <XAxis dataKey="cohortMonth" stroke="var(--color-text-muted)" fontSize={11} />
          <YAxis yAxisId="days" stroke="var(--color-text-muted)" fontSize={11} />
          {hasPerPoint && (
            <YAxis
              yAxisId="perPoint"
              orientation="right"
              stroke="var(--color-text-muted)"
              fontSize={11}
            />
          )}
          <Tooltip
            contentStyle={{
              backgroundColor: 'var(--color-bg-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: '8px',
            }}
            labelStyle={{ color: 'var(--color-text-primary)' }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Line
            yAxisId="days"
            type="monotone"
            dataKey="p50Days"
            name={t('kpi.pmoFlow.trend.p50Days')}
            stroke={P50_COLOR}
            strokeWidth={2}
            dot={false}
          />
          {/*
            p90 は p50 より一桁大きくなることがあり、同じ軸に出すと改善の主指標である
            p50 が軸の下端に潰れる。既定では隠し、長い尾を見たいときだけ重ねる
          */}
          {showP90 && (
            <Line
              yAxisId="days"
              type="monotone"
              dataKey="p90Days"
              name={t('kpi.pmoFlow.trend.p90Days')}
              stroke={P90_COLOR}
              strokeWidth={2}
              dot={false}
            />
          )}
          {hasPerPoint && (
            <Line
              yAxisId="perPoint"
              type="monotone"
              dataKey="p50PointsPerDay"
              name={t('kpi.pmoFlow.trend.p50PointsPerDay')}
              stroke={PER_POINT_COLOR}
              strokeWidth={2}
              strokeDasharray="4 3"
              dot={false}
              connectNulls
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * フェーズ区間の月次コホート推移カード。既定は「案件あたりの待ち合計」で、
 * セレクトから計測できている区間へ切り替えられる。選択対象の p50 / p90 日数と
 * （あれば）pt 正規化 p50 を第2軸で重ねて表示する。
 * 改善が効いているかを月単位で確認するためのカード。
 *
 * 待ち合計のときは p90 を既定で出す。待ちは右裾が長く、p50 は既に底に張り付いていて
 * 改善余地が読めないため。系列自体もバックエンド側で 3 ヶ月移動窓になっている
 * （単月の母数は 4〜27 件しかなく、単月 p90 は外れ値 1 件で跳ねる）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMOフェーズリードタイムの月次コホート推移カード。計測可能な区間をセレクトで選び、p50/p90日数とpt正規化p50の推移を折れ線表示する
 * @graph-connects PhaseTrendChart [calls] 月次コホート折れ線描画
 * @graph-connects Card [calls] カードコンポーネント描画
 * @graph-connects Select [calls] 区間セレクト描画
 */
export function PhaseTrendCard({ segments, months }: PhaseTrendCardProps) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<PmoFlowTrendTarget | null>(null);
  const [showP90, setShowP90] = useState<boolean | null>(null);

  const measurable = sortPmoFlowSegments(segments).filter(hasMeasuredDays);
  // 待ち合計を先頭かつ既定にする。区間ごとの内訳より「案件が合計何日止まっていたか」が
  // 減らしたい対象そのもので、月次で追うべき主系列だから
  const options = [
    { value: PMO_FLOW_WAIT_TOTAL_TARGET, label: t('kpi.pmoFlow.trend.waitTotalOption') },
    ...measurable.map((segment) => ({
      value: segment.key,
      label: t(`kpi.pmoFlow.segment.${segment.key}`),
    })),
  ];
  // 選択中の区間が計測不能になった場合は待ち合計へ戻す（待ち合計は常に選べる）
  const effective: PmoFlowTrendTarget =
    selected &&
    (selected === PMO_FLOW_WAIT_TOTAL_TARGET || measurable.some((s) => s.key === selected))
      ? selected
      : PMO_FLOW_WAIT_TOTAL_TARGET;
  const isWaitTotal = effective === PMO_FLOW_WAIT_TOTAL_TARGET;
  // 待ち合計では p90 が主指標なので既定で出す。区間別は従来どおり既定で隠す
  const effectiveShowP90 = showP90 ?? isWaitTotal;

  return (
    <Card className="animate-content-reveal">
      <CardHeader
        title={t('kpi.pmoFlow.trend.title')}
        description={t(
          isWaitTotal ? 'kpi.pmoFlow.trend.waitTotalDescription' : 'kpi.pmoFlow.trend.description',
          { months },
        )}
        action={
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5 text-xs text-[var(--color-text-muted)]">
              <Checkbox
                checked={effectiveShowP90}
                onChange={(e) => {
                  setShowP90(e.target.checked);
                }}
              />
              {t('kpi.pmoFlow.trend.showP90')}
            </label>
            <Select
              options={options}
              value={effective}
              onChange={(e) => {
                setSelected(e.target.value as PmoFlowTrendTarget);
              }}
              size="sm"
              aria-label={t('kpi.pmoFlow.trend.segmentLabel')}
            />
          </div>
        }
      />
      <CardContent>
        <PhaseTrendChart segment={effective} months={months} showP90={effectiveShowP90} />
      </CardContent>
    </Card>
  );
}
